import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { SolePanel, SolePanelData } from './sole-panel.interface';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LoadingOverlayService } from '@shared/data-access/services';
import { Logger } from 'libs/log/data-access/src/lib/service/logger.service';
import { EventNames } from './solepanel-logging.enum';
import { isPlatformBrowser } from '@angular/common';
import { Chain, Environment } from '@shared/data-access/interfaces';
import { SolePanelPostMessageAction } from '@cart-checkout/data-access/enums';
import { CheckoutPostMessageAction } from './checkout-postmessage-interface';
import { CookieService, WINDOW } from '@shared/utils/services';
@Injectable({
	providedIn: 'root'
})
export class SolePanelService {

	listeningForPostMessages = false;
	enablePlaceOrderButton = false;

	private actionDebounce$ = new Subject();
	private solePanelObject: SolePanel;

	constructor(
		@Inject(PLATFORM_ID) private platformId,
		@Inject(WINDOW) private window: Window,
		private environment: Environment,
		private cookieService: CookieService,
		private chain: Chain,
		private router: Router,
		private loadingOverlayService: LoadingOverlayService,
		protected logger: Logger
	) { }

	/**
	 * @description
	 * Subscribe to debouncer as it is awaiting messages.
	 * Only subscribes if single-page.component.ts if solePanelEnabled killswitch enabled
	 * and insideSolePanel() returns true;
	 */
	handleSolePanelDebouncer(): void {
		// For when we update the contact info, we need to debounce the call.
		this.actionDebounce$
		.pipe(
			// Debounce by half a second
			debounceTime(500)
		)
		.subscribe((solePanelData: SolePanelData) => {
			// Required to update SolePanel when contact changes
			this.sendMessageToSolePanel(solePanelData);
		});
	}

	/**
	 * @description
	 * Debounce wrapper for 'sendMessageToSolePanel'
	 */
	deboucedSendMessageToSolePanel(contactInfo: SolePanelData): void {
		this.actionDebounce$.next(contactInfo);
	}

	insideSolePanel(): boolean{
		if (!isPlatformBrowser(this.platformId)) return false;
		// Check if it is located in an iframe
		// Check if the origin is a sole panel page
		const isInAnIframe = this.window.location !== this.window.parent.location;
		const isSolePanelPage = this.window.location.href.includes(this.environment?.solePanel[this.chain.chainMarketingIdentifierAbbr]);

		return isInAnIframe && isSolePanelPage;
	}

	sendMessageToSolePanel(solePanelData: SolePanelData, action = SolePanelPostMessageAction.UPDATE_ORDER_DATA): void {
		if (!isPlatformBrowser(this.platformId)) return;

		// Verify if it is inside the sole panel and if there is a change in the object.
		const canSendMessage = this.insideSolePanel() && this.verifyObject(solePanelData, action);

		if (!canSendMessage) { return; }

		if (!this.solePanelObject) {
			this.solePanelObject = {
				action,
				origin: 'CHECKOUT-UI',
				data: {
					checkoutKey: this.cookieService.get('X-DCSG-CHECKOUT'),
					accessKey: this.cookieService.get('DCSG-CHECKOUT-ACCESS'),
					cart: this.cookieService.get('DCSG-CART'),
				}
			};
		}

		this.solePanelObject = {
			action,
			origin: 'CHECKOUT-UI',
			data: {
				...this.solePanelObject?.data,
				...solePanelData,
			}
		};

		// The event is only sent to iframe parent
		this.window.parent.postMessage(this.solePanelObject, this.environment.solePanel.iframeParent);

		this.handleLogging(this.solePanelObject.data, action, EventNames.SENT_TO_SOLE_PANEL);

		if (this.solePanelObject.action === SolePanelPostMessageAction.ORDER_PLACED) {
			this.resetSolePanelObject();
		}
	}

	listenForSolePanelChanges(): void {
		if (!isPlatformBrowser(this.platformId)) return;

		// Listen to post message events
		this.listeningForPostMessages = true;
		this.window.addEventListener('message', this.handleMessageFromSolePanel.bind(this));
		this.sendMessageToSolePanel({}, SolePanelPostMessageAction.EVENT_LISTENER_READY);
	}

	private handleLogging(solePanelData: SolePanelData, action: string, eventName: string): void {
		const logObject = { eventName: eventName, eventAction: action, eventLabel: 'SolePanelEvent' };
		if (solePanelData.orderNumber) {
			logObject['orderNumber'] = solePanelData.orderNumber;
		}
		this.logger.log('SolePanel Activity', logObject);
	}

	/**
	 * @description Handle post message response from sole panel
	 */
	private handleMessageFromSolePanel(event: MessageEvent): void {
		// Using '*' for non-prod env so that AI can test their changes locally
		if (this.environment.solePanel.iframeParent !== '*' && event.origin !== this.environment.solePanel.iframeParent) return;

		const solePanelData = event.data;

		switch (solePanelData.action) {
			case CheckoutPostMessageAction.SET_CART:
				this.loadingOverlayService.show();
				this.cookieService.set('DCSG-CART', solePanelData.data.cookie, 1, '/', null, null, true);
				this.handleLogging(solePanelData, solePanelData.action, EventNames.RECEIVED_FROM_SOLE_PANEL);
				this.reload();
			break;

			case CheckoutPostMessageAction.CLEAR_CART:
				this.loadingOverlayService.show();
				this.cookieService.deleteAll('/', this.window.location.hostname); // Getting the domain from hostname
				this.enablePlaceOrderButton = false;
				this.handleLogging(solePanelData, solePanelData.action, EventNames.RECEIVED_FROM_SOLE_PANEL);
				this.reload();
			break;

			case CheckoutPostMessageAction.ENABLE_PLACE_ORDER:
				this.enablePlaceOrderButton = true;
				this.handleLogging(solePanelData, solePanelData.action, EventNames.RECEIVED_FROM_SOLE_PANEL);
			break;
		}
	}

	private resetSolePanelObject(): void {
		this.solePanelObject = undefined;
		this.enablePlaceOrderButton = false;
	}

	private verifyObject(checkout: SolePanelData, action: SolePanelPostMessageAction): boolean {

		// Check if the object has not been created yet
		if (!this.solePanelObject) {
			return true;
		}

		// Check if the action has changed
		if (this.solePanelObject.action !== action){
			return true;
		}

		// Check if the values of the checkout have changed
		const keys = Object.keys(checkout);

		for (const key of keys) {
			if (checkout[key] !== this.solePanelObject.data[key]) {
			  return true;
			}
		}

		// There are no changes in the object
		return false;
	}


	/**
	 * @description An efficient way to reload the web page
	 */
	private reload() {
		// If we're on the cart page then reload the route
		// otherwise nagivate back to the cart page
		if (this.router.url.includes('OrderItemDisplay')) {
			// Need a follow up ticket to update our usage of routeReuseStrategy since it's now deprecated
			// Save for later also uses routeReuseStrategy
			this.router.routeReuseStrategy.shouldReuseRoute = () => false;
			this.router.navigate(['OrderItemDisplay'], {
				skipLocationChange: false,
				queryParamsHandling: 'merge'
			});
		}
		else {
			this.window.location.href = '/OrderItemDisplay';
		}
	}
}
